import { useState } from 'react'
import { BarChart3, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Bar,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { JarvisCard } from '@/components/JarvisCard'
import { StatCard } from '@/components/StatCard'
import { ScoreBadge } from '@/components/ScoreBadge'
import { ConnectionPrompt } from '@/components/ConnectionPrompt'
import { useAdsReport, useAdsTrend } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import { formatCents, formatNumber, formatPercent, formatRoas } from '@/lib/format'
import type { AdReportItem, AdScore } from '@/types'
import { cn } from '@/lib/cn'

function AdItemCard({ item }: { item: AdReportItem }) {
  return (
    <div className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-jarvis-muted">
            {item.entityType}
          </div>
          <div className="text-sm font-medium truncate">{item.entityName}</div>
        </div>
        {item.score && <ScoreBadge score={item.score} />}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-jarvis-muted tabular">
        <span>
          Spend{' '}
          <span className="text-jarvis-text">{formatCents(item.spend * 100)}</span>
        </span>
        <span>
          ROAS{' '}
          <span className="text-jarvis-text">{formatRoas(item.roas)}</span>
        </span>
        {item.cpl > 0 && (
          <span>
            CPL <span className="text-jarvis-text">{formatCents(item.cpl * 100)}</span>
          </span>
        )}
        <span>
          Freq <span className="text-jarvis-text">{item.frequency.toFixed(2)}</span>
        </span>
        {item.hookRate !== null && (
          <span>
            Hook <span className="text-jarvis-text">{formatPercent(item.hookRate)}</span>
          </span>
        )}
      </div>
      {item.reason && (
        <div className="pt-2 border-t border-jarvis-border/50 text-sm">
          <span className="text-jarvis-blue text-xs uppercase tracking-widest mr-2">
            Jarvis
          </span>
          {item.reason}
          {item.action && (
            <div className="text-jarvis-muted mt-1">
              <span className="text-xs uppercase tracking-widest mr-2">Action</span>
              {item.action}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ScoreSection({
  score,
  title,
  items,
  defaultOpen = true,
}: {
  score: AdScore
  title: string
  items: AdReportItem[]
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  if (items.length === 0) return null
  return (
    <div className="space-y-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-3">
          <ScoreBadge score={score} />
          <span className="text-sm font-medium">
            {title} <span className="text-jarvis-muted">({items.length})</span>
          </span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((item) => (
            <AdItemCard key={`${item.entityType}-${item.entityId}`} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}

export function FacebookAds() {
  const [range, setRange] = useState<'last_7d' | 'last_30d'>('last_7d')
  const queryClient = useQueryClient()
  const { data, isLoading } = useAdsReport(range)
  const trend = useAdsTrend(range === 'last_7d' ? 'last_7d' : 'last_30d')

  const sync = useMutation({
    mutationFn: () => unwrap<unknown>(apiClient.post('/ads/sync')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ads'] }),
  })

  const items = data?.topItems ?? []
  const pause = items.filter((i) => i.score === 'pause')
  const scale = items.filter((i) => i.score === 'scale')
  const watch = items.filter((i) => i.score === 'watch')

  // Real time-series — one point per snapshotDate, blended across campaigns.
  // Falls back to per-entity rendering only if no historical series exists.
  const trendData = (trend.data?.series ?? []).map((p) => ({
    name: p.date.slice(5),
    spend: Math.round(p.spend),
    roas: Number(p.roas.toFixed(2)),
  }))

  return (
    <div className="space-y-6">
      <ConnectionPrompt
        required="facebook"
        label="Facebook Ads"
        description="Add your Meta system user token in Setup so Jarvis can sync campaigns."
      />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Ads Intelligence</h1>
        <div className="flex items-center gap-2">
          <div className="flex bg-jarvis-surface-2 border border-jarvis-border rounded-lg p-1">
            {(['last_7d', 'last_30d'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={cn(
                  'px-3 py-1 text-xs rounded-md transition-colors',
                  range === r
                    ? 'bg-jarvis-blue/20 text-jarvis-blue'
                    : 'text-jarvis-muted hover:text-jarvis-text',
                )}
              >
                {r === 'last_7d' ? '7d' : '30d'}
              </button>
            ))}
          </div>
          <button
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            className="flex items-center gap-2 px-3 py-1.5 text-xs bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-surface transition-colors"
          >
            <RefreshCw size={14} className={sync.isPending ? 'animate-spin' : ''} />
            Sync
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="ROAS"
          value={data?.totals.blendedRoas ? formatRoas(data.totals.blendedRoas) : '—'}
        />
        <StatCard
          label="Spend"
          value={data?.totals.spend ? formatCents(data.totals.spend * 100) : '—'}
        />
        <StatCard
          label="Blended CPL"
          value={data?.totals.blendedCpl ? formatCents(data.totals.blendedCpl * 100) : '—'}
        />
        <StatCard
          label="Active Items"
          value={data?.topItems.length ? formatNumber(data.topItems.length) : '—'}
        />
      </div>

      {isLoading && (
        <JarvisCard>
          <div className="text-jarvis-muted text-sm py-8 text-center">
            Loading ads data…
          </div>
        </JarvisCard>
      )}

      {!isLoading && items.length === 0 && (
        <JarvisCard>
          <div className="text-center py-10 px-4">
            <BarChart3 size={28} className="mx-auto text-jarvis-muted/60 mb-3" />
            <p className="text-sm">No ad snapshots yet.</p>
            <p className="text-xs text-jarvis-muted mt-1">
              Connect your Meta system user token and Jarvis will pull campaigns automatically.
            </p>
            <div className="mt-4 flex gap-2 justify-center">
              <Link
                to="/setup"
                className="text-xs px-3 py-1.5 rounded-md bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright transition-colors"
              >
                Connect Facebook
              </Link>
              <button
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                className="text-xs px-3 py-1.5 rounded-md border border-jarvis-border hover:bg-jarvis-surface-2 transition-colors"
              >
                Run a sync
              </button>
            </div>
          </div>
        </JarvisCard>
      )}

      <div className="space-y-6">
        <ScoreSection score="pause" title="Pause Recommended" items={pause} />
        <ScoreSection score="scale" title="Scale" items={scale} />
        <ScoreSection
          score="watch"
          title="Watch"
          items={watch}
          defaultOpen={false}
        />
      </div>

      {trendData.length > 0 && (
        <JarvisCard>
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-4">
            Spend & ROAS — daily trend ({range === 'last_7d' ? '7d' : '30d'})
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData}>
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} />
                <YAxis yAxisId="left" stroke="#6B7280" fontSize={11} />
                <YAxis yAxisId="right" orientation="right" stroke="#6B7280" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: '#0F172A',
                    border: '1px solid #1F2937',
                    borderRadius: 8,
                  }}
                />
                <Bar yAxisId="left" dataKey="spend" fill="rgba(59,130,246,0.4)" />
                <Line
                  yAxisId="right"
                  dataKey="roas"
                  stroke="#06B6D4"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </JarvisCard>
      )}
    </div>
  )
}
